/**
 * Whether the Leaderboard option is switched on, as an admin set it (Admin → Leaderboard mode). The setting lives
 * on the relay so it applies to every player's device; this reads it from `/config` and remembers the last answer
 * so Mode Select can draw instantly without waiting for the network.
 *
 * Fails closed: with no answer ever received (a first visit while offline, an expired session) the option stays
 * hidden. Leaderboard games need the network anyway, so nothing is lost by hiding it when the relay can't be reached.
 */

import { getSessionToken } from './activation';

// Same base-URL pattern as src/ui/activation.ts.
const RELAY_BASE = import.meta.env?.VITE_VISION_RELAY_URL ?? '/api';

const CACHE_KEY = 'mahjong-scorer:leaderboardEnabled';

/** The last answer the relay gave this device — false if it never has (or storage is unavailable). */
export function cachedLeaderboardEnabled(): boolean {
  try {
    return localStorage.getItem(CACHE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * What the Terms of Use should assume: the last answer the relay gave, or — with no answer ever received — that the leaderboard IS on
 * (`undefined` here). Unknown must mean the FULL terms: showing fewer promises than may apply is the wrong direction to be wrong in.
 */
export function knownLeaderboardEnabled(): boolean | undefined {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    return stored === 'true' ? true : stored === 'false' ? false : undefined;
  } catch {
    return undefined;
  }
}

/** Asks the relay whether the leaderboard is on WITHOUT a session (the activation screen) and remembers the answer. */
export async function refreshPublicLeaderboardEnabled(): Promise<boolean | undefined> {
  try {
    const res = await fetch(`${RELAY_BASE}/public-config`, { cache: 'no-store' });
    if (!res.ok) return undefined;
    const body = (await res.json()) as { leaderboardEnabled?: unknown };
    if (typeof body.leaderboardEnabled !== 'boolean') return undefined;
    remember(body.leaderboardEnabled);
    return body.leaderboardEnabled;
  } catch {
    return undefined;
  }
}

function remember(enabled: boolean): void {
  try {
    localStorage.setItem(CACHE_KEY, String(enabled));
  } catch {
    /* private mode, storage full — the next visit just asks again */
  }
}

/**
 * Asks the relay and remembers the answer. Resolves `undefined` when there is no answer (offline, no session,
 * relay error) — the caller then keeps whatever it already shows.
 */
export async function refreshLeaderboardEnabled(): Promise<boolean | undefined> {
  const token = getSessionToken();
  if (!token) return undefined;
  try {
    const res = await fetch(`${RELAY_BASE}/config`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
    if (!res.ok) return undefined;
    const body = (await res.json()) as { leaderboardEnabled?: unknown };
    const enabled = body.leaderboardEnabled === true;
    remember(enabled);
    return enabled;
  } catch {
    return undefined;
  }
}
